/**
 * ChatMedPaciente.tsx
 *
 * Componente de chat P2P entre médico e paciente usando PeerJS (WebRTC Data Channel).
 *
 * Lógica de conexão:
 * - Médico: peer ID = `doc-{appointmentId}`, conecta ao peer `pat-{appointmentId}`
 * - Paciente: peer ID = `pat-{appointmentId}`, conecta ao peer `doc-{appointmentId}`
 *
 * Ambos os lados tentam se conectar mutuamente para que a conexão seja
 * estabelecida independentemente de quem abrir o chat primeiro.
 *
 * A janela de chat é flutuante e arrastável (desktop) ou modal (mobile),
 * seguindo o mesmo padrão visual do VideoConferencia.
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Peer, { DataConnection } from "peerjs";
import { useSessionContext } from "../../contexts/SessionContext";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  DragHandle as DragHandleIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import Draggable from "react-draggable";
import { isMobile } from "react-device-detect";
import Portal from "@mui/material/Portal";
import type { Theme } from "@mui/material/styles";

/** z-index bem acima de modais/snackbars do MUI; o chat sempre “ganha” do restante da página. */
const CHAT_PORTAL_Z_INDEX_MODAL_DELTA = 12_500;

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface ChatMessage {
  /** ID único da mensagem para evitar duplicatas */
  id: string;
  text: string;
  senderName: string;
  /** true = mensagem do próprio usuário (exibe à direita) */
  isOwn: boolean;
  timestamp: string;
}

type ConnectionStatus = "disconnected" | "connecting" | "connected";

export interface ChatMedPacienteProps {
  /**
   * CPF do paciente (sem formatação, apenas dígitos).
   * Usado para derivar os peer IDs da sala PeerJS.
   * - Médico: recebe como prop (extraído do encounterReference)
   * - Paciente: lido automaticamente de user.username no SessionContext
   */
  patientCpf?: string;
  /** Papel do usuário atual nesta sessão */
  role: "doctor" | "patient";
  /**
   * Se true, renderiza apenas o ícone de chat (para uso em cardSchedule/StepArea desktop).
   * Se false (padrão), renderiza botão completo com texto.
   */
  cardMode?: boolean;
  /** Desabilita o botão quando o atendimento está pendente (ex: aguardando pagamento) */
  isPending?: boolean;
  /**
   * FluxVet — linha de ações do card (Próximas Consultas): mesmo perfil dos outros botões
   * (altura 36px, sem radius, outlined na cor primária verde como teleatendimento).
   */
  fluxVetCardActions?: boolean;
}

// ─── Utilitários ──────────────────────────────────────────────────────────────

/**
 * Sanitiza um identificador para uso como peer ID no PeerJS.
 * PeerJS aceita apenas: letras, números, hífen e underscore.
 * Limitamos a 60 chars para evitar peer IDs muito longos.
 */
function sanitizePeerId(id: string): string {
  return id.replace(/[^a-zA-Z0-9\-_]/g, "").slice(0, 60);
}

/**
 * Extrai apenas os dígitos numéricos de uma string.
 * Útil para limpar CPFs que venham com formatação (ex: "123.456.789-01").
 */
function extractDigits(value: string): string {
  return value.replace(/\D/g, "");
}

// ─── Componente ───────────────────────────────────────────────────────────────

const ChatMedPaciente: React.FC<ChatMedPacienteProps> = ({
  patientCpf,
  role,
  cardMode = false,
  isPending = false,
  fluxVetCardActions = false,
}) => {
  // Lê os dados do usuário atual diretamente do contexto de sessão.
  // user.name  = nome completo (string SCIM2: "givenName familyName")
  // user.username = CPF do usuário logado (apenas dígitos)
  const { user } = useSessionContext();

  /**
   * Nome de exibição nas mensagens do chat.
   * Vem de user.name (SCIM2), que já é uma string com nome completo.
   * Para profissionais, adiciona o prefixo "Dr(a). " antes do nome.
   * Fallback por role caso o nome não esteja disponível.
   */
  const baseName: string =
    (user?.name as string | undefined) ||
    (role === "patient" ? "Paciente" : "Médico");

  const displayName: string =
    role === "doctor" ? `Dr(a). ${baseName}` : baseName;

  /**
   * CPF do paciente que identifica a sala P2P.
   * - Paciente: usa o próprio CPF da sessão (user.username)
   * - Médico: recebe via prop patientCpf (extraído do encounterReference)
   * Remove formatação para garantir consistência nos peer IDs.
   */
  const resolvedPatientCpf: string =
    role === "patient"
      ? extractDigits((user?.username as string | undefined) || "")
      : extractDigits(patientCpf || "");

  /** z-index do overlay do chat: acima de modais MUI quando renderizado via Portal em document.body */
  const chatLayerZIndex = (theme: Theme) =>
    theme.zIndex.modal + CHAT_PORTAL_Z_INDEX_MODAL_DELTA;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("disconnected");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  /** Obrigatório no react-draggable 4.x: evita reattach do DOM e “piscada” ao mover o mouse (Strict Mode/hover). */
  const draggableNodeRef = useRef<HTMLDivElement | null>(null);
  /** Atualiza só no resize/abertura — bounds novo a cada render forçava o Draggable a recalcular e tremer */
  const [dragBoundsRect, setDragBoundsRect] = useState(() => ({
    left: 10,
    top: 10,
    right: Math.max(10, typeof window !== "undefined" ? window.innerWidth - 370 : 10),
    bottom: Math.max(10, typeof window !== "undefined" ? window.innerHeight - 510 : 10),
  }));

  // Refs para PeerJS — não são estados pois não precisam disparar re-render
  const peerRef = useRef<Peer | null>(null);
  const connRef = useRef<DataConnection | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Evita tentar re-reconectar quando o componente está sendo desmontado
  const isClosingRef = useRef(false);

  /**
   * Peer IDs derivados do CPF do paciente.
   * Médico: `doc-{cpf}`, Paciente: `pat-{cpf}`
   * O mesmo CPF é conhecido por ambos os lados, garantindo que se encontrem.
   */
  const safeId = sanitizePeerId(resolvedPatientCpf);
  const myPeerId = role === "doctor" ? `doc-${safeId}` : `pat-${safeId}`;
  const targetPeerId = role === "doctor" ? `pat-${safeId}` : `doc-${safeId}`;

  // Auto-scroll sempre que novas mensagens chegarem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ─── Limpeza ao desmontar o componente ─────────────────────────────────────

  useEffect(() => {
    return () => {
      isClosingRef.current = true;
      destroyPeer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Inicializa janela ao abrir ────────────────────────────────────────────

  useEffect(() => {
    if (isOpen && !isMobile) {
      // Bounds estáveis só mudam ao abrir / resize — evita “piscada” do react-draggable
      setDragBoundsRect({
        left: 10,
        top: 10,
        right: Math.max(10, window.innerWidth - 370),
        bottom: Math.max(10, window.innerHeight - 510),
      });
      // Centraliza a janela de chat na tela
      const x = Math.max(0, (window.innerWidth - 360) / 2);
      const y = Math.max(0, (window.innerHeight - 500) / 2);
      setPosition({ x, y });
      initializePeer();
    } else if (isOpen && isMobile) {
      initializePeer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Ao redimensionar com o chat aberto (desktop), recalcula limites sem recriar a cada hover
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const onResize = () => {
      setDragBoundsRect({
        left: 10,
        top: 10,
        right: Math.max(10, window.innerWidth - 370),
        bottom: Math.max(10, window.innerHeight - 510),
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isOpen, isMobile]);

  // ─── Funções PeerJS ────────────────────────────────────────────────────────

  /** Destrói o peer e a conexão atual com segurança */
  const destroyPeer = () => {
    if (connRef.current) {
      try {
        connRef.current.close();
      } catch (_) {
        // Ignora erros ao fechar conexão já encerrada
      }
      connRef.current = null;
    }
    if (peerRef.current) {
      try {
        peerRef.current.destroy();
      } catch (_) {
        // Ignora erros ao destruir peer já destruído
      }
      peerRef.current = null;
    }
  };

  /**
   * Configura listeners em uma conexão existente (entrada ou saída).
   * Centraliza o tratamento de dados e desconexão.
   */
  const setupConnectionListeners = useCallback(
    (conn: DataConnection) => {
      conn.on("open", () => {
        setConnectionStatus("connected");
      });

      conn.on("data", (data: unknown) => {
        // Mensagem recebida do outro lado — marca como não-própria
        const msg = data as ChatMessage;
        setMessages((prev) => [...prev, { ...msg, isOwn: false }]);
      });

      conn.on("close", () => {
        if (!isClosingRef.current) {
          setConnectionStatus("disconnected");
        }
      });

      conn.on("error", (err) => {
        console.warn("[ChatMedPaciente] Erro na conexão:", err);
        if (!isClosingRef.current) {
          setConnectionStatus("disconnected");
        }
      });
    },
    []
  );

  /**
   * Tenta se conectar ao peer remoto.
   * Mantém tentativas periódicas caso o outro lado ainda não esteja online.
   */
  const connectToPeer = useCallback(
    (peer: Peer) => {
      if (isClosingRef.current) return;
      setConnectionStatus("connecting");

      try {
        const conn = peer.connect(targetPeerId, { reliable: true });
        connRef.current = conn;
        setupConnectionListeners(conn);
      } catch (err) {
        console.warn("[ChatMedPaciente] Falha ao conectar:", err);
        // Tenta novamente em 5 segundos
        setTimeout(() => {
          if (!isClosingRef.current && peerRef.current) {
            connectToPeer(peerRef.current);
          }
        }, 5000);
      }
    },
    [targetPeerId, setupConnectionListeners]
  );

  /**
   * Inicializa o peer PeerJS com o ID do usuário atual.
   * Aceita conexões de entrada e também tenta conectar ao outro lado.
   */
  const initializePeer = useCallback(() => {
    isClosingRef.current = false;
    destroyPeer();

    const peer = new Peer(myPeerId, {
      // debug: 1, // Descomente para depuração
    });
    peerRef.current = peer;
    setConnectionStatus("connecting");

    peer.on("open", () => {
      // Peer criado com sucesso — tenta conectar ao outro lado
      connectToPeer(peer);
    });

    // Aceita conexões de entrada (quando o outro lado conecta primeiro)
    peer.on("connection", (conn: DataConnection) => {
      connRef.current = conn;
      setupConnectionListeners(conn);
    });

    peer.on("error", (err) => {
      if (err.type === "unavailable-id") {
        // ID já em uso (ex: aba duplicada) — aguarda conexão de entrada
        setConnectionStatus("connecting");
      } else if (err.type === "peer-unavailable") {
        // Outro lado ainda não está online — tenta novamente
        setTimeout(() => {
          if (!isClosingRef.current && peerRef.current) {
            connectToPeer(peerRef.current);
          }
        }, 5000);
      } else {
        console.warn("[ChatMedPaciente] Erro no Peer:", err);
        if (!isClosingRef.current) {
          setConnectionStatus("disconnected");
        }
      }
    });
  }, [myPeerId, connectToPeer, setupConnectionListeners]);

  // ─── Ações do usuário ──────────────────────────────────────────────────────

  /** Fecha o chat e destrói a conexão PeerJS */
  const handleClose = () => {
    isClosingRef.current = true;
    setIsOpen(false);
    destroyPeer();
    setConnectionStatus("disconnected");
    setMessages([]);
  };

  /** Envia a mensagem atual para o peer remoto */
  const sendMessage = () => {
    const text = inputText.trim();
    if (!text || !connRef.current || connectionStatus !== "connected") return;

    const message: ChatMessage = {
      id: `${Date.now()}-${Math.random()}`,
      text,
      senderName: displayName,
      isOwn: true,
      timestamp: new Date().toISOString(),
    };

    // Envia ao peer remoto (com isOwn=false pois é recebido pelo outro lado)
    connRef.current.send({ ...message, isOwn: false });
    // Adiciona localmente como própria
    setMessages((prev) => [...prev, message]);
    setInputText("");
  };

  /** Permite enviar com Enter (Shift+Enter para nova linha) */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Renderização do botão ─────────────────────────────────────────────────

  const renderButton = () => {
    if (cardMode) {
      // Versão compacta: apenas ícone (usado em CardSchedule e StepArea desktop)
      return (
        <Button
          variant="text"
          onClick={() => setIsOpen(true)}
          disabled={isPending}
          sx={{
            minWidth: "auto",
            padding: 0,
            color: isPending ? "neutral700.main" : "neutral900.main",
            "&:hover": {
              backgroundColor: "transparent",
              color: isPending ? "neutral800.main" : "primary800.main",
            },
          }}
        >
          <ChatIcon sx={{ fontSize: 24 }} />
        </Button>
      );
    }

    const chatDisabled = isPending || !resolvedPatientCpf.trim();

    // FluxVet card: outlined + ícone + texto, alinhado a tele/cancelar/pagamento (sem border-radius)
    if (fluxVetCardActions) {
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setIsOpen(true)}
          disabled={chatDisabled}
          sx={{
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            height: 36,
            borderRadius: 0,
            textTransform: "uppercase",
            fontWeight: 500,
            gap: 0.5,
            borderColor: chatDisabled ? "neutral400.main" : "#00A884",
            color: chatDisabled ? "neutral600.main" : "#00A884",
            "&:hover": {
              borderColor: chatDisabled ? undefined : "#008c6e",
              bgcolor: chatDisabled ? undefined : "rgba(0, 168, 132, 0.04)",
            },
          }}
        >
          <ChatIcon fontSize="small" />
          <Typography
            variant="body2"
            component="span"
            sx={{ textTransform: "uppercase", fontWeight: 500 }}
          >
            Iniciar chat
          </Typography>
        </Button>
      );
    }

    // Versão completa: mesmo padrão do botão "Iniciar Teleatendimento"
    // (VideoConferencia) — verde preenchido (primary700) com texto branco para
    // destaque, hover em verde mais escuro, e tipografia igual ao label do Button.
    return (
      <Button
        variant="outlined"
        onClick={() => setIsOpen(true)}
        disabled={chatDisabled}
        sx={{
          width: "100%",
          textTransform: "none",
          gap: 1,
          borderRadius: 2,
          minHeight: 38,
          fontWeight: 500,
          color: chatDisabled ? "neutral600.main" : "#fff",
          borderColor: chatDisabled ? "neutral400.main" : "primary700.main",
          backgroundColor: chatDisabled ? "transparent" : "primary700.main",
          "&:hover": {
            borderColor: chatDisabled ? "neutral400.main" : "primary800.main",
            backgroundColor: chatDisabled ? "transparent" : "primary800.main",
          },
        }}
      >
        <ChatIcon
          sx={{
            color: chatDisabled ? "neutral600.main" : "#fff",
            fontSize: 22,
            flexShrink: 0,
          }}
        />
        <Typography
          component="span"
          color={chatDisabled ? "neutral600.main" : "#fff"}
          sx={{
            whiteSpace: "nowrap",
            fontSize: "0.875rem",
            fontWeight: 500,
            letterSpacing: "0.02857em",
            lineHeight: 1.75,
          }}
        >
          {role === "patient" ? "Acessar Chat" : "Iniciar Chat"}
        </Typography>
      </Button>
    );
  };

  // ─── Indicadores de status ─────────────────────────────────────────────────

  const statusColorMap: Record<ConnectionStatus, string> = {
    disconnected: "error.main",
    connecting: "warning.main",
    connected: "success.main",
  };

  const statusLabelMap: Record<ConnectionStatus, string> = {
    disconnected: "Desconectado",
    connecting: "Conectando...",
    connected: "Conectado",
  };

  // ─── Conteúdo interno da janela de chat ───────────────────────────────────

  const renderChatContent = () => (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Cabeçalho: título, status de conexão e botão fechar */}
      <Box
        className="chat-header"
        sx={{
          cursor: isMobile ? "default" : "move",
          bgcolor: "primary800.main",
          p: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {!isMobile && (
            <DragHandleIcon sx={{ color: "neutral0.main", fontSize: 18 }} />
          )}
          <ChatIcon sx={{ color: "neutral0.main", fontSize: 20 }} />
          <Typography variant="subtitle2" color="neutral0.main">
            Chat
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Largura mínima evita mudanças de layout texto/spinner ao alternar estado (sensação de “piscada”). */}
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              flexShrink: 0,
              minWidth: { xs: 110, sm: 128 },
              justifyContent: "flex-end",
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {connectionStatus === "connecting" ? (
                <CircularProgress size={14} sx={{ color: "warning.light" }} />
              ) : (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: statusColorMap[connectionStatus],
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="neutral0.main" noWrap>
              {statusLabelMap[connectionStatus]}
            </Typography>
          </Stack>

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ color: "error.main" }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Área de mensagens */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          bgcolor: "grey.50",
        }}
      >
        {/* Estado vazio: aguardando conexão */}
        {messages.length === 0 && connectionStatus === "connecting" && (
          <Stack alignItems="center" justifyContent="center" flex={1} gap={1}>
            <CircularProgress size={24} />
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Aguardando o outro participante conectar...
            </Typography>
          </Stack>
        )}

        {/* Estado vazio: conectado mas sem mensagens ainda */}
        {messages.length === 0 && connectionStatus === "connected" && (
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            mt={2}
          >
            Conexão estabelecida. Envie uma mensagem para começar.
          </Typography>
        )}

        {/* Estado vazio: desconectado */}
        {messages.length === 0 && connectionStatus === "disconnected" && (
          <Typography
            variant="caption"
            color="error.main"
            textAlign="center"
            mt={2}
          >
            Sem conexão. Aguardando o outro participante.
          </Typography>
        )}

        {/* Lista de mensagens */}
        {messages.map((msg, index) => {
          // Esconde o nome quando a mensagem anterior é do mesmo remetente
          const prevMsg = messages[index - 1];
          const showName = !prevMsg || prevMsg.senderName !== msg.senderName;

          return (
          <Stack
            key={msg.id}
            alignItems={msg.isOwn ? "flex-end" : "flex-start"}
          >
            {/* Nome do remetente — exibido apenas na primeira mensagem de cada sequência */}
            {showName && (
              <Typography variant="caption" color="text.secondary" px={0.5}>
                {msg.senderName}
              </Typography>
            )}

            {/* Balão de mensagem */}
            <Box
              sx={{
                maxWidth: "80%",
                bgcolor: msg.isOwn ? "primary700.main" : "white",
                color: msg.isOwn ? "white" : "text.primary",
                borderRadius: msg.isOwn
                  ? "12px 12px 2px 12px"
                  : "12px 12px 12px 2px",
                px: 1.5,
                py: 0.75,
                boxShadow: 1,
              }}
            >
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {msg.text}
              </Typography>
              {/* Horário da mensagem */}
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.7,
                  fontSize: "0.65rem",
                  display: "block",
                  textAlign: "right",
                }}
              >
                {new Date(msg.timestamp).toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>
          </Stack>
          );
        })}

        {/* Ancora para auto-scroll */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Campo de entrada + botão enviar */}
      <Box
        sx={{
          p: 1,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
          flexShrink: 0,
          bgcolor: "background.paper",
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={3}
          size="small"
          placeholder="Digite sua mensagem..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={connectionStatus !== "connected"}
          variant="outlined"
        />
        <IconButton
          onClick={sendMessage}
          disabled={!inputText.trim() || connectionStatus !== "connected"}
          size="small"
          sx={{
            bgcolor: "primary700.main",
            color: "white",
            flexShrink: 0,
            mb: 0.25,
            "&:hover": { bgcolor: "primary800.main" },
            "&.Mui-disabled": { bgcolor: "grey.300", color: "grey.500" },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  // ─── Janela de chat (mobile = modal fixo / desktop = draggable) ───────────

  const renderChatWindow = () => {
    if (isMobile) {
      // Versão mobile: modal centralizado fixo na tela
      return (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: chatLayerZIndex,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "90%", height: "70vh" }}>
            {renderChatContent()}
          </Box>
        </Box>
      );
    }

    // Versão desktop: janela flutuante e arrastável (nodeRef + bounds estáveis evitam piscadas no hover/move)
    return (
      <Draggable
        handle=".chat-header"
        nodeRef={draggableNodeRef}
        position={position}
        onStop={(_, data) => setPosition({ x: data.x, y: data.y })}
        bounds={dragBoundsRect}
      >
        <Box
          ref={draggableNodeRef}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: chatLayerZIndex,
            width: "360px",
            height: "500px",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 3,
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
            isolation: "isolate",
          }}
        >
          {renderChatContent()}
        </Box>
      </Draggable>
    );
  };

  // ─── Render principal ──────────────────────────────────────────────────────

  /**
   * Portal em document.body evita stacking context dos cards/paper (transform, filtros etc.):
   * o modal do chat pinta sempre por cima do restante e o hit-area não “briga” com o hover dos cards.
   */
  return (
    <>
      {renderButton()}
      {isOpen ? <Portal>{renderChatWindow()}</Portal> : null}
    </>
  );
};

export default ChatMedPaciente;
